import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { openFileInNewTab } from 'src/app/shared/utils/file-link.util';
import { Component, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { forkJoin, from, Subject } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { Sample } from 'src/app/models/sample.model';
import { LabRequisition } from 'src/app/models/labRequisition.model';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { IhLabResult } from 'src/app/models/IhLabResult.model';
import { SampleService } from 'src/app/services/sample.service';
import { LabService } from 'src/app/services/lab.service';
import { PdfGeneratorService } from 'src/app/components/report-generation-popup/services/pdf-generator.service';
import { ChainOfCustodyPdfService } from '../generate-coc-popup/chain-of-custody-pdf.service';
import { LabRequisitionService } from 'src/app/services/lab-requisition.service';
import { ToastService } from 'src/app/services/toast.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { CommonService } from 'src/app/services/common.service';
import { AuthService } from 'src/app/services';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import { SampleStatusEnum, SampleEditableFields, getFilteredSampleStatuses, isSampleStatusSelectable, getStatusBadgeClass } from 'src/app/enums/sampleStatus.enum';
import { SampleStatusOption } from 'src/app/models/sampleStatusOption.model';
import { SecurityProfileEnum } from 'src/app/enums/securityProfile.enum';
import { Units } from 'src/app/enums/units.enum';
import { TWACorrectionMethod } from 'src/app/enums/TWACorrectionMethod.enum';
import { UnlinkCocDialogComponent } from 'src/app/modals/unlink-coc-dialog/unlink-coc-dialog.component';
import { LabResultImportDialogComponent } from 'src/app/modals/lab-result-import-dialog/lab-result-import-dialog.component';
import { LabResultImportDialogData } from 'src/app/modals/lab-result-import-dialog/lab-result-import.model';
import { LabResultImportService, labResultTemplateFileName } from 'src/app/modals/lab-result-import-dialog/lab-result-import.service';
import { DOWNLOAD_BLOB_URL_REVOKE_DELAY_MS, downloadBlob } from 'src/app/shared/utils/download.util';
import { IVoidReasonResult, VoidReasonDialogComponent } from 'src/app/modals/void-reason-dialog/void-reason-dialog.component';
import nameOf from 'src/app/shared/form/nameOf.helper';

interface SampleStatusPatchRequest {
    sampleStatus: SampleStatusEnum;
    voidReason?: string;
}

interface SampleRowData {
    sample: Sample;
    agent: IhLabResult | null;
    isFirstRow: boolean;
    rowspan: number;
    canUnlink: boolean;
    statusLabel: string;
    statusBadgeClass: string;
    statusOptions: SampleStatusOption[];
}

interface DetailField {
    id: string;
    label: string;
    value: string;
}

interface NestedColumn {
    key: string;
    label: string;
    getValue: (agent: IhLabResult) => string;
}

@Component({
  standalone: false,
    selector: 'app-lab-requisition-details-popup',
    templateUrl: './lab-requisition-details-popup.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [ChainOfCustodyPdfService, PdfGeneratorService],
    styleUrl: './lab-requisition-details-popup.component.scss',
})
export class LabRequisitionDetailsPopupComponent implements OnInit, OnDestroy {
    labRequisitionDetails: LabRequisition;
    massColKey = nameOf<IhLabResult>(x => x.totalMass);
    displayedColumns: string[] = ['sampleId', 'agent', this.massColKey, 'concentration', 'unsampledTimeCorrection',
        'unsampledTimeConcentration', 'twaCorrectionMethod', 'twa', 'status', 'actions'];
    showBlankCorrectedMass = false;
    isGeneratingCoc = false;
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    dataSource = new MatTableDataSource<SampleRowData>();
    isLoading = false;
    totalSamples = 0;
    detailFields: DetailField[] = [];
    nestedColumns: NestedColumn[] = [];
    private currentSamples: Sample[] = [];
    private allStatuses: { id: number; name: string }[] = [];
    private destroy$ = new Subject<void>();
    private paginatorInitialized = false;

    constructor(
        @Inject(FLYOUT_DATA) public labRequisitionId: string,
        private labRequisitionService: LabRequisitionService,
        private sampleService: SampleService,
        private toastService: ToastService,
        private dateFormatService: DateFormatService,
        private valueConversionService: ValueConversionService,
        private commonService: CommonService,
        private authService: AuthService,
        private labResultImportService: LabResultImportService,
        private labService: LabService,
        private cocPdf: ChainOfCustodyPdfService,
        protected dialog: MatDialog,
    ) { }

    ngOnInit(): void {
        this.initializeStatusOptions();
        this.initializeNestedColumns();
        this.loadData();
    }

    private setupPaginatorSubscription(): void {
        if (this.paginator && !this.paginatorInitialized) {
            this.paginatorInitialized = true;
            this.paginator.page.pipe(takeUntil(this.destroy$)).subscribe(() => {
                this.loadPagedData();
            });
        }
    }

    private initializeStatusOptions(): void {
        this.allStatuses = Object.keys(SampleStatusEnum)
            .filter(key => !isNaN(Number(SampleStatusEnum[key])))
            .map(key => ({
                id: SampleStatusEnum[key],
                name: this.commonService.getEnumName(SampleStatusEnum, SampleStatusEnum[key])
            }));
    }

    private initializeNestedColumns(): void {
        this.nestedColumns = [
            { key: this.massColKey, label: 'Mass', getValue: (agent) => this.getMass(agent) },
            { key: 'concentration', label: 'Concentration', getValue: (agent) => this.getConcentration(agent) },
            { key: 'unsampledTimeCorrection', label: 'Unsampled Time Correction', getValue: (agent) => this.getUnsampledTimeCorrection(agent) },
            { key: 'unsampledTimeConcentration', label: 'Unsampled Time Concentration', getValue: (agent) => this.getUnsampledTimeConcentration(agent) },
            { key: 'twaCorrectionMethod', label: 'TWA Correction Method', getValue: (agent) => this.getTwaCorrectionMethod(agent) },
            { key: 'twa', label: 'TWA', getValue: (agent) => this.getTwa(agent) }
        ];
    }

    private updateDetailFields(): void {
        this.detailFields = [
            { id: 'received', label: 'Received', value: '' },
            { id: 'due', label: 'Due', value: this.formatDate(this.labRequisitionDetails?.emailEdd) },
            { id: 'survey', label: 'Survey', value: '-' },
            { id: 'site', label: 'Site', value: '-' },
        ];
    }

    loadData(): void {
        this.isLoading = true;
        const filterBy = JSON.stringify({ labRequisitionId: this.labRequisitionId });

        forkJoin({
            labRequisitionDetails: this.labRequisitionService.getById(this.labRequisitionId),
            samples: this.sampleService.get('', 'id', 'asc', 0, 10, filterBy)
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
                this.labRequisitionDetails = response.labRequisitionDetails;
                this.updateDetailFields();
                this.totalSamples = response.samples.count;
                this.processSamples(response.samples.data);
                this.isLoading = false;

                setTimeout(() => {
                    if (this.paginator) {
                        this.paginator.length = this.totalSamples;
                        this.setupPaginatorSubscription();
                    }
                });
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
            }
        });
    }

    loadPagedData(): void {
        const filterBy = JSON.stringify({ labRequisitionId: this.labRequisitionId });
        const { pageIndex = 0, pageSize = 10 } = this.paginator || {};

        this.sampleService.get('', 'id', 'asc', pageIndex, pageSize, filterBy).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
                this.totalSamples = response.count;
                this.processSamples(response.data);
                if (this.paginator) {
                    this.paginator.length = this.totalSamples;
                }
            },
            error: (error) => {
                this.toastService.showError(error);
            }
        });
    }

    processSamples(samples: Sample[]): void {
        this.currentSamples = samples;
        const isAdmin = this.isAdminUser();
        const flattenedData: SampleRowData[] = samples.flatMap(sample => this.buildSampleRows(sample, isAdmin));

        this.dataSource.data = flattenedData;
        this.updateBlankCorrectedMassVisibility(flattenedData);
    }

    private isAdminUser(): boolean {
        const currentUser = this.authService.getCurrentUser();
        return !!currentUser && [SecurityProfileEnum.Admin, SecurityProfileEnum.SuperAdmin].includes(currentUser.userType);
    }

    private buildSampleRows(sample: Sample, isAdmin: boolean): SampleRowData[] {
        const agents: (IhLabResult | null)[] = sample.ihLabResults?.length ? sample.ihLabResults : [null];
        const statusOptions = getFilteredSampleStatuses(this.allStatuses, sample.sampleType, sample.sampleCategory, sample.sampleStatus)
            .filter(status => isSampleStatusSelectable(sample.sampleStatus, status.id))
            .map(status => ({ ...status, badgeClass: getStatusBadgeClass(status.id) }));
        const shared = {
            canUnlink: isAdmin && !SampleEditableFields.nonEditable.includes(sample.sampleStatus),
            statusLabel: statusOptions.find(option => option.id === sample.sampleStatus)?.name ?? this.getStatusLabel(sample.sampleStatus),
            statusBadgeClass: getStatusBadgeClass(sample.sampleStatus),
            statusOptions
        };

        return agents.map((agent, i) => ({
            ...shared,
            sample,
            agent,
            isFirstRow: i === 0,
            rowspan: agents.length
        }));
    }

    private refreshSample(sample: Sample): void {
        const isAdmin = this.isAdminUser();
        this.currentSamples = this.currentSamples.map(s => s.id === sample.id ? sample : s);
        this.dataSource.data = this.dataSource.data.flatMap(row => {
            if (row.sample.id !== sample.id) return [row];
            return row.isFirstRow ? this.buildSampleRows(sample, isAdmin) : [];
        });
    }

    private getSavedSample(sample: Sample): Sample {
        return this.currentSamples.find(s => s.id === sample.id) ?? sample;
    }

    private updateBlankCorrectedMassVisibility(data: SampleRowData[]): void {
        if (this.showBlankCorrectedMass) return;
        const hasBlankCorrection = data.some(row => row.agent?.blankCorrectionApplied);
        if (hasBlankCorrection) {
            this.showBlankCorrectedMass = true;
            const blankColKey = nameOf<IhLabResult>(x => x.blankCorrectedMass);
            const massIndex = this.displayedColumns.indexOf(this.massColKey);
            this.displayedColumns.splice(massIndex + 1, 0, blankColKey);
            this.nestedColumns.splice(1, 0, {
                key: blankColKey,
                label: 'Blank Corrected Mass',
                getValue: (agent) => this.getBlankCorrectedMass(agent)
            });
        }
    }

    getBlankCorrectedMass(agent: IhLabResult): string {
        if (!agent?.blankCorrectionApplied || agent?.blankCorrectedMass == null) return '-';
        const massValue = agent.agentUnits === Units.MgPerM3
            ? this.valueConversionService.convertUgPerM3ToMgPerM3(agent.blankCorrectedMass)
            : agent.blankCorrectedMass;
        const massUnit = agent.agentUnits === Units.MgPerM3 ? 'mg' : 'µg';
        return `${massValue.toFixed(4)} ${massUnit}`;
    }

    formatDate(date: Date | string | number): string {
        if (!date) return '-';
        try {
            return this.dateFormatService.convertUTCDateStringToUserTimeZoneString(
                date.toString(),
                this.dateFormatService.userDateFormat
            );
        } catch {
            return '-';
        }
    }

    getStatusLabel(status: SampleStatusEnum): string {
        return this.commonService.getEnumName(SampleStatusEnum, status) || '-';
    }

    onStatusChange(sample: Sample, newStatus: SampleStatusEnum): void {
        if (newStatus === SampleStatusEnum.Void) {
            const dialogRef = this.dialog.open(VoidReasonDialogComponent, {
                data: { voidReason: sample.voidReason || '' },
                width: '400px',
            });
            dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: string | IVoidReasonResult) => {
                const voidReason = typeof result === 'string'
                    ? result
                    : result?.confirmed
                        ? (result.voidReason || '')
                        : '';
                if (voidReason) {
                    this.performStatusUpdate(sample, newStatus, voidReason);
                } else {
                    this.refreshSample(this.getSavedSample(sample));
                }
            });
        } else {
            this.performStatusUpdate(sample, newStatus);
        }
    }

    private performStatusUpdate(sample: Sample, newStatus: SampleStatusEnum, voidReason?: string): void {
        const updatePayload: SampleStatusPatchRequest = { sampleStatus: newStatus };
        if (voidReason) {
            updatePayload.voidReason = voidReason;
        }
        this.sampleService.patch(sample.id, updatePayload).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                const saved = this.getSavedSample(sample);
                this.refreshSample({ ...saved, sampleStatus: newStatus, voidReason: voidReason ?? saved.voidReason });
                this.toastService.showSuccess('Sample status updated successfully', true);
            },
            error: (error) => {
                this.refreshSample(this.getSavedSample(sample));
                this.toastService.showError(error);
            }
        });
    }

    private formatValueWithUnit(value: number | undefined, agent: IhLabResult): string {
        if (!value) return '-';
        const convertedValue = this.valueConversionService.getParameterValueAsPerAgentUnit(value, agent.agentUnits);
        const unit = this.valueConversionService.getParameterUnitSymbol(agent.agentUnits);
        return `${convertedValue.toFixed(4)} ${unit}`;
    }

    getMass(agent: IhLabResult): string {
        if (!agent?.totalMass) return '-';
        const massValue = agent.agentUnits === Units.MgPerM3
            ? this.valueConversionService.convertUgPerM3ToMgPerM3(agent.totalMass)
            : agent.totalMass;
        const massUnit = agent.agentUnits === Units.MgPerM3 ? 'mg' : 'µg';
        return `${massValue.toFixed(4)} ${massUnit}`;
    }

    getConcentration(agent: IhLabResult): string {
        return this.formatValueWithUnit(agent?.concValue, agent);
    }

    getUnsampledTimeConcentration(agent: IhLabResult): string {
        return this.formatValueWithUnit(agent?.unsampledTimeConcValue, agent);
    }

    getTwa(agent: IhLabResult): string {
        return this.formatValueWithUnit(agent?.twa, agent);
    }

    getTwaCorrectionMethod(agent: IhLabResult): string {
        return agent?.twaCorrectionMethod != null ? TWACorrectionMethod[agent?.twaCorrectionMethod] : '-';
    }

    getUnsampledTimeCorrection(agent: IhLabResult): string {
        return agent?.unsampledTimeConcValue ? 'Yes' : 'No';
    }

    openImportResults(): void {
        // Only Admin/SuperAdmin may import results — mirrors the commit authorization at the BE and the
        // guard on other sensitive actions in this component (the row's precomputed canUnlink flag). Blocks exposing the full
        // mapping/analysis UI to unauthorized users before any authorization is asserted.
        const currentUser = this.authService.getCurrentUser();
        if (!currentUser || ![SecurityProfileEnum.Admin, SecurityProfileEnum.SuperAdmin].includes(currentUser.userType)) {
            this.toastService.displayErrorMessage('You do not have permission to import lab results', true);
            return;
        }
        const data: LabResultImportDialogData = {
            labRequisitionId: this.labRequisitionId,
            labRequisitionNumber: this.labRequisitionDetails?.labRequisitionNumber
        };
        const dialogRef = this.dialog.open(LabResultImportDialogComponent, {
            data,
            width: '900px',
            maxHeight: '90vh',
            disableClose: true
        });
        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((imported: boolean) => {
            if (imported) {
                this.loadData();
            }
        });
    }

    // Admin/SuperAdmin only — the template endpoint is Admin-gated on the BE, so the button is hidden for everyone
    // else (mirrors openImportResults / the row canUnlink flag) rather than exposing an always-403 control.
    get canDownloadResultTemplate(): boolean {
        const currentUser = this.authService.getCurrentUser();
        return !!currentUser && [SecurityProfileEnum.Admin, SecurityProfileEnum.SuperAdmin].includes(currentUser.userType);
    }

    // DASH-2722: regenerate and download the empty lab-result template on demand, at any requisition status.
    // Nothing is persisted — each request rebuilds the CSV from current requisition data.
    downloadResultTemplate(): void {
        const fileName = labResultTemplateFileName(this.labRequisitionDetails?.labRequisitionNumber || String(this.labRequisitionId));
        this.labResultImportService.downloadTemplate(this.labRequisitionId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (blob) => downloadBlob(blob, fileName),
            // Localized message (es-CL included); the raw BE error is a Blob showError can't render meaningfully.
            error: () => this.toastService.displayErrorMessage('Lab result template could not be generated', true)
        });
    }

    viewCurrentCoc(): void {
        const requisition = this.labRequisitionDetails;
        this.isGeneratingCoc = true;
        forkJoin({
            lab: this.labService.getById(requisition.labId),
            samples: this.sampleService.getByIds(requisition.sampleIds ?? [])
        }).pipe(
            switchMap(({ lab, samples }) => from(this.cocPdf.render({ requisition, lab, samples, isPreview: true }))),
            finalize(() => this.isGeneratingCoc = false),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (blob) => {
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank', 'noopener,noreferrer');
                setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_BLOB_URL_REVOKE_DELAY_MS);
            },
            error: (error) => this.toastService.showError(error)
        });
    }

    openAttachment(attachment: FileAttachment): void {
        if (!openFileInNewTab(attachment.url)) {
            this.toastService.displayErrorMessage('File URL not available. Please contact support', true);
        }
    }

    onUnlinkSample(element: SampleRowData): void {
        const dialogRef = this.dialog.open(UnlinkCocDialogComponent, {
            data: {
                sampleNumber: element.sample.sampleNumber,
                labReqNumber: this.labRequisitionDetails?.labRequisitionNumber,
                sampleId: element.sample.id
            },
            width: '500px',
            disableClose: true
        });

        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result === true) {
                if (this.paginator && this.dataSource.data.filter(r => r.sample.id !== element.sample.id).length === 0 && this.paginator.pageIndex > 0) {
                    this.paginator.pageIndex--;
                }
                this.loadPagedData();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public trackByIndex = trackByIndex;
}